import get from 'lodash/get';

// Based on https://schema.org/Person JSON-LD structure
function getAuthorLDSchema(user) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Person',
    name: user.username,
  };
}

// Based on https://schema.org/Review JSON-LD structure
function getReviewLDSchema(review) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Review',
    author: getAuthorLDSchema(review.user),
    datePublished: review.submit_date,
    reviewBody: review.comment_escaped,
    name: review.title_escaped,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating?.toString(),
      bestRating: '5',
      worstRating: '1',
    },
  };
}

// Based on https://schema.org/Product JSON-LD structure
function getProductDetailSchema(deal) {
  const endDate = deal.dates.end_date ? deal.dates.end_date : deal.dates.schema_end_date;

  const productLDSchema = {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name: deal.public_name,
    brand: {
      '@type': 'Brand',
      name: deal.public_name,
    },
    mpn: deal.id.toString(),
    sku: deal.id.toString(),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: deal.deal_review.average_rating?.toString() || '0',
      reviewCount: deal.deal_review.review_count?.toString() || '0',
    },
    review: deal.top_5_reviews.map(getReviewLDSchema),
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      url: `https://appsumo.com${deal.get_absolute_url}`,
      price: deal.default_price.toString(),
      availabilityStarts: deal.dates.start_date ? deal.dates.start_date : '',
      availabilityEnds: endDate,
      priceValidUntil: endDate,
      availability: deal.is_redeemable ? 'http://schema.org/InStock' : 'http://schema.org/SoldOut',
      seller: {
        '@type': 'Organization',
        name: 'AppSumo',
      },
    },
  };

  if (deal.products[0]?.story?.subheader) {
    productLDSchema.description = deal.products[0]?.story?.subheader;
  }

  if (deal.media_url) {
    productLDSchema.image = {
      '@type': 'ImageObject',
      url: deal.media_url,
      height: '266',
      width: '800',
    };
  }

  return productLDSchema;
}

function getBreadcrumbLDSchema(items) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: (i + 1),
      name: item.name,
      ...i < items.length - 1 ? { item: item.url } : {},
    })),
  };
}

// Based on https://schema.org/Question JSON-LD structure
function getQuestionLDSchema(question, deal) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Question',
    name: question.comment_escaped,
    text: question.comment_escaped,
    answerCount: question.children.length,
    upvoteCount: question.num_likes,
    author: getAuthorLDSchema(question.user),
    discussionUrl: question.display_path,
    isBasedOn: getProductDetailSchema(deal),
  };
}

// Based on https://schema.org/Answer JSON-LD structure
function getAnswerLDSchema(answer, questionPath) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Answer',
    text: answer.comment_escaped,
    upvoteCount: answer.num_likes,
    url: `${questionPath}${answer.id}`,
    author: getAuthorLDSchema(answer.user),
  };
}

// Based on https://schema.org/VideoObject JSON-LD structure
function getVideoObjectLDSchema(deal) {
  const story = get(deal, ['products', 0, 'story'], {});
  const { videos } = story;

  const validVideos = videos?.map((video) => {
    if (video?.embed_code?.indexOf('youtube.com') >= 0) {
      const matches = video.embed_code.match(/<iframe[^>]*src=["|']([^'"]+)["|'][^>]*>/);

      if (matches?.length === 2) {
        return {
          ...video,
          embed_url: matches[1],
        };
      }
    }

    return false;
  }).filter(Boolean);

  if (!validVideos?.length) return [];

  return validVideos?.map((video) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    description: story.meta_title || story.card_description,
    name: deal.public_name,
    thumbnailUrl: video.embed_thumbnails?.md,
    embedUrl: video.embed_url,
    uploadDate: deal.dates?.start_date,
  }));
}

export function getBreadcrumbLDSchemaJSONString(items) {
  return JSON.stringify(getBreadcrumbLDSchema(items), null, '\t');
}

export function getTaxonomyBreadcrumbLDSchemaJSONString({
  collection, group, category, subcategory, deal,
}) {
  const items = [];

  if (collection) {
    const root = collection.root_level ? '/' : '/collections';
    items.push({
      url: `https://appsumo.com${root}/${collection.slug}/`,
      name: collection.title,
    });
  } else if (group) {
    items.push({
      url: `https://appsumo.com/${group.value}/`,
      name: group.name,
    });

    if (category) {
      items.push({
        url: `https://appsumo.com/${group.value}/${category.value}/`,
        name: category.name,
      });

      if (subcategory) {
        items.push({
          url: `https://appsumo.com/${group.value}/${category.value}/${subcategory.value}/`,
          name: subcategory.name,
        });
      }
    }

    if (deal) {
      items.push({
        name: deal.public_name,
      });
    }
  }

  return getBreadcrumbLDSchemaJSONString(items);
}

// Based on https://schema.org/Product#eg-0010 JSON-LD structure
export function getProductPageLDSchemaJSONString(deal) {
  return JSON.stringify(getProductDetailSchema(deal), null, '\t');
}

export function getVideoObjectLDSchemaJSONString(deal) {
  const schema = getVideoObjectLDSchema(deal);

  if (schema) {
    return JSON.stringify(schema, null, '\t');
  }

  return null;
}

// Based on https://schema.org/Review JSON-LD structure
// https://developers.google.com/search/docs/advanced/structured-data/review-snippet#examples
export function getReviewPageLDSchemeJSONString(review, deal) {
  const reviewSchema = getReviewLDSchema(review);

  // add info of the Product this review belongs
  reviewSchema.itemReviewed = getProductDetailSchema(deal);

  return JSON.stringify(reviewSchema, null, '\t');
}

// https:// developers.google.com/search/docs/advanced/structured-data/qapage?hl=en#question
export function getQuestionPageLDSchemeJSONString(question, deal) {
  const questionSchema = {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: getQuestionLDSchema(question, deal),
  };

  if (question.children.length) {
    questionSchema.mainEntity.suggestedAnswer = [
      ...question.children.map((answer) => getAnswerLDSchema(answer, question.display_path)),
    ];
  }

  return JSON.stringify(questionSchema, null, '\t');
}

export default {
  getTaxonomyBreadcrumbLDSchemaJSONString,
  getBreadcrumbLDSchemaJSONString,
  getProductPageLDSchemaJSONString,
  getReviewPageLDSchemeJSONString,
  getQuestionPageLDSchemeJSONString,
  getVideoObjectLDSchemaJSONString,
};
